<script setup>
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import OverlaySending from '../organisms/overlay/OverlaySending.vue';
import LayoutDefault from '../layouts/LayoutDefault.vue';
import { useGdprTextsStore } from '../../stores/gdpr-texts-store';
import { saveApplication } from '../../services/applicant';
import { EVENTS, FUNNEL_STEP, FUNNEL_TYPES, track } from '../../utils/tracking';
import config from '../../config';
import { PAGE_FORM, PAGE_SUCCESS } from '../../routes';
import * as logger from '../../utils/logger';
import { useApplicationStore } from '../../stores/application-store';
import { usePayloadStore } from '../../stores/payload-store';
import { useErrorStore } from '../../stores/error-store';
import { useProfile } from '../../stores/profile-store';
import { useJobDataStore } from '../../stores/job-store';
import { log } from '../../utils/actionLogger';
import { useWebViewContext } from '../../composables/webview-context';
import { useAuth } from '../../stores/auth-store';

const root = ref(null);
const router = useRouter();
const auth = useAuth();

/* Web view */
const { isWebView, deeplinkToAppRoute } = useWebViewContext();

function loginButtonExists() {
    return !!root.value?.$el.querySelector('.mf-header__loginButton');
}

function hideLoginButton() {
    if (loginButtonExists()) {
        root.value.$el.querySelector('.mf-header__loginButton').classList.add('k-state--hidden');
    }
}

function showLoginButton() {
    if (loginButtonExists()) {
        root.value.$el.querySelector('.mf-header__loginButton').classList.remove('k-state--hidden');
    }
}

function fetchRecaptchaToken() {
    return new Promise((resolve, reject) => {
        if (config.googleRecaptcha.active) {
            try {
                window.grecaptcha.ready(() => {
                    window.grecaptcha.execute(config.googleRecaptcha.siteKey)
                        .then((token) => {
                            resolve(token);
                        });
                });
            } catch (e) {
                reject();
            }
        } else {
            // Needed for staging and local environment, if recaptcha is turned off.
            // There are problems with acceptance tests, when this value is boolean, because this
            // value is then set as recaptcha response into the store and later on set as request header.
            resolve('true');
        }
    });
}

function isLauraHeidelbeereException(exception) {
    return exception.response?.data?.errors?.[0]?.title === 'Nice try, Laura Heidelbeere.';
}

const application = useApplicationStore();
const payload = usePayloadStore();
const errors = useErrorStore();
const profile = useProfile();
const jobData = useJobDataStore();

const documentsTrackingElementDetail = computed(() => {
    if (application.anyProfileDocumentIsInActiveAttachments && application.anyUploadedDocumentIsInActiveAttachments) {
        return 'profile,custom';
    } if (application.anyProfileDocumentIsInActiveAttachments) {
        return 'profile';
    } if (application.anyUploadedDocumentIsInActiveAttachments) {
        return 'custom';
    }
    return 'none';
});

const cvTrackingElementDetail = computed(() => {
    if (auth.isLoggedIn) {
        return profile.hasProfile ? 'with profile' : 'logged-in';
    }

    return 'logged-out';
});

async function apply() {
    try {
        const token = await fetchRecaptchaToken();
        // If the recaptcha is not valid, show the general error.
        if (!token) return;

        application.recaptchaResponse = token;
        try {
            const gdprTexts = useGdprTextsStore();

            log('on-application-form-successful-frontend-validation');

            await saveApplication({
                application,
                gdprTexts: gdprTexts.$state,
                payload: payload.$state,
                profile: profile.$state,
                customFields: jobData.serializedCustomFields,
                meta: {
                    clientApplicationUuid: application.applicationUuid,
                },
            });

            track({
                element: 'AP_F: Submit - Success',
                elementDetail: 'Ad Click - smart bewerben button',
            });

            // CV selection tracking
            track({
                element: `AP_F: CV: ${application.selectedCvType}`,
                elementDetail: `User: ${cvTrackingElementDetail.value}`,
            });

            // Other documents tracking
            track({
                element: `AP_F: Documents: ${documentsTrackingElementDetail.value}`,
                elementDetail: `User: ${cvTrackingElementDetail.value}`,
            });

            if (application.showApplicationLetter) {
                track({
                    element: 'AP_F: Submit - Success',
                    elementDetail: 'Remarks',
                });
            }

            track({
                event: EVENTS.funnel,
                funnelType: FUNNEL_TYPES.application,
                funnelStep: FUNNEL_STEP.success,
            });

            track({
                event: EVENTS.jobApplySuccess,
                jobId: payload.jobId,
                jobType: jobData.isSeamless ? 'SJA' : 'Standard',
                productType: jobData.productType,
                applicationType: 'SmartBewerben',
                userData: {
                    email: profile.email ?? application.email ?? null,
                },
            });

            if (application.hasSeenPreview) {
                track({
                    element: 'AP_F: Submit - Success',
                    elementDetail: 'Seen preview',
                });
            }

            if (application.isMailDifferentFromAccount) {
                track({
                    element: 'AP_F: Email different from profile',
                });
            }

            if (application.isFirstnameDifferentFromAccount) {
                track({
                    element: 'AP_F: Firstname different from profile',
                });
            }

            if (application.isLastnameDifferentFromAccount) {
                track({
                    element: 'AP_F: Surname different from profile',
                });
            }

            if (application.isSalutationDifferentFromAccount) {
                track({
                    element: 'AP_F: Salutation different from profile',
                });
            }

            if (isWebView.value) {
                deeplinkToAppRoute('application-success');
                return;
            }

            await router.replace({
                name: PAGE_SUCCESS,
            });
        } catch (e) {
            if (isLauraHeidelbeereException(e)) {
                errors.lauraHeidelbeereException = true;
            } else {
                errors.general = true;
            }

            // Re-fetch custom fields in case something has changed
            await jobData.fetchCustomFields(payload.jobId);
            await router.replace({
                name: PAGE_FORM,
            });
        }
    } catch (exception) {
        await router.replace({
            name: PAGE_FORM,
        });
        errors.general = true;
        logger.error({
            exception,
            fingerprint: ['Fetching recaptcha failed.'],
            message: 'Fetching recaptcha failed.',
        });
    }
}

onBeforeMount(hideLoginButton);
onBeforeUnmount(showLoginButton);
onMounted(apply);
</script>

<template>
    <LayoutDefault ref="root">
        <template #main>
            <OverlaySending/>
        </template>
    </LayoutDefault>
</template>
